import React from 'react';
import { Input, Select, Button, DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoginLogFilters } from '../hooks/useLoginLogQuery';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface LoginLogFilterProps {
  filters: LoginLogFilters;
  onChange: (filters: LoginLogFilters) => void;
  onFilter: () => void;
  onReset: () => void;
}

const LoginLogFilter: React.FC<LoginLogFilterProps> = ({ filters, onChange, onFilter, onReset }) => {
  const { t } = useTranslation();

  const handleTimeRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onChange({
        ...filters,
        startTime: dates[0].startOf('day').unix(),
        endTime: dates[1].endOf('day').unix(),
      });
    } else {
      onChange({ ...filters, startTime: undefined, endTime: undefined });
    }
  };

  const timeRange: [Dayjs, Dayjs] | undefined =
    filters.startTime && filters.endTime
      ? [dayjs.unix(filters.startTime), dayjs.unix(filters.endTime)]
      : undefined;

  return (
    <div className="mb-2 py-2">
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div className="min-w-0">
          <div className="mb-1 text-sm text-gray-600">{t('supportTools.loginLog.filters.phone')}</div>
          <Input
            size="middle"
            placeholder={t('supportTools.loginLog.filters.phonePlaceholder') ?? ''}
            value={filters.phone}
            onChange={(e) => onChange({ ...filters, phone: e.target.value })}
            allowClear
          />
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-sm text-gray-600">{t('supportTools.loginLog.filters.operationType')}</div>
          <Select
            size="middle"
            className="w-full"
            placeholder={t('supportTools.loginLog.filters.all') ?? ''}
            value={filters.operationType}
            onChange={(val) => onChange({ ...filters, operationType: val })}
            allowClear
            options={[
              { label: t('supportTools.loginLog.operationTypes.PASSWORD_LOGIN'), value: 'PASSWORD_LOGIN' },
              { label: t('supportTools.loginLog.operationTypes.VERIFICATION_CODE_LOGIN'), value: 'VERIFICATION_CODE_LOGIN' },
              { label: t('supportTools.loginLog.operationTypes.SUPPORT_LOGIN'), value: 'SUPPORT_LOGIN' },
              { label: t('supportTools.loginLog.operationTypes.LOGOUT'), value: 'LOGOUT' },
            ]}
          />
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-sm text-gray-600">{t('supportTools.loginLog.filters.loginResult')}</div>
          <Select
            size="middle"
            className="w-full"
            placeholder={t('supportTools.loginLog.filters.all') ?? ''}
            value={filters.loginResult}
            onChange={(val) => onChange({ ...filters, loginResult: val })}
            allowClear
            options={[
              { label: t('supportTools.loginLog.results.SUCCESS'), value: 'SUCCESS' },
              { label: t('supportTools.loginLog.results.FAILURE'), value: 'FAILURE' },
            ]}
          />
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-sm text-gray-600">{t('supportTools.loginLog.filters.clientType')}</div>
          <Select
            size="middle"
            className="w-full"
            placeholder={t('supportTools.loginLog.filters.all') ?? ''}
            value={filters.clientType}
            onChange={(val) => onChange({ ...filters, clientType: val })}
            allowClear
            options={[
              { label: 'PC', value: 'PC' },
              { label: 'APP', value: 'APP' },
              { label: 'PDA', value: 'PDA' },
              { label: 'UNKNOWN', value: 'UNKNOWN' },
            ]}
          />
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-sm text-gray-600">{t('supportTools.loginLog.filters.timeRange')}</div>
          <RangePicker
            size="middle"
            className="w-full"
            value={timeRange}
            onChange={handleTimeRangeChange}
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="primary" onClick={onFilter}>
          <strong>{t('supportTools.loginLog.filters.search')}</strong>
        </Button>
        <Button onClick={onReset}>
          <strong>{t('supportTools.loginLog.filters.reset')}</strong>
        </Button>
      </div>
    </div>
  );
};

export default LoginLogFilter;
